import { InfoCircleBigIcon } from '@app/components/icons/info-circle-big';
import { useTranslation } from '@hopin-team/provider-translation';
import React from 'react';

import { Button } from '../button/button';
import { Tooltip } from '../tooltip/tooltip';

type CardProps = {
  heading: string;
  subHeading?: string;
  tooltip?: string;
  loading?: boolean;
  showFooter?: boolean;
  onViewAll?: () => void;
  children: React.ReactNode;
};

export const CardWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="rounded-[14px] border border-neutral-b4 px-6 pt-5 pb-6 shadow-sm relative flex flex-col overflow-hidden max-w-full h-full">
      {children}
    </div>
  );
};

export const Header = ({
  heading,
  tooltip,
}: {
  heading: string;
  tooltip?: string;
}) => {
  return (
    <div className="relative font-inter text-[17px] font-medium leading-[25px] truncate mb-1">
      {heading}
      {tooltip && (
        <div className="absolute right-0 inline-block">
          <Tooltip tooltip={tooltip} placement="bottom-center">
            <InfoCircleBigIcon />
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export const SubHeader = ({ subHeading }: { subHeading: string }) => {
  return (
    <div className="mb-4 break-normal font-inter text-xs text-neutral-b1 font-normal leading-[1.125rem]">
      {subHeading}
    </div>
  );
};

export const ViewAll = ({
  onViewAll,
  loading,
}: {
  loading?: boolean;
  onViewAll?: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <div className="pt-2 border-t border-neutral-b4">
      <Button
        className="!px-0 hover:bg-transparent"
        inline
        pattern="ghost"
        size="medium"
        onClick={onViewAll}
        disabled={loading}
      >
        <span className={'font-medium font-inter'}>
          {t('analytics.view-all')}
        </span>
      </Button>
    </div>
  );
};

export const Card = ({
  heading,
  subHeading,
  tooltip,
  children,
  loading,
  showFooter,
  onViewAll,
}: CardProps) => {
  return (
    <CardWrapper>
      <Header heading={heading} tooltip={tooltip} />
      {subHeading && <SubHeader subHeading={subHeading} />}
      {children}
      {showFooter && <ViewAll onViewAll={onViewAll} loading={loading} />}
    </CardWrapper>
  );
};
